import React, { useEffect, useId, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Pause from '@mui/icons-material/Pause';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Save from '@mui/icons-material/Save';
import Undo from '@mui/icons-material/Undo';
import Redo from '@mui/icons-material/Redo';
import SkipNext from '@mui/icons-material/SkipNext';
import SkipPrevious from '@mui/icons-material/SkipPrevious';
import Repeat from '@mui/icons-material/Repeat';
import ContentCut from '@mui/icons-material/ContentCut';
import ImageFrameSlider from './ImageFrameSlider';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { describeImageTiming, timelineStartTimes } from '../image_utilities/Timeline';
import { imageShape } from '../shapes/ImageShapes';
import ToolTippedIconButton from './UI/ToolTippedIconButton';

const Player = ({
  images,
  highlightedImageName = null,
  totalNumFrames,
  absoluteCurrentIndex,
  onClickPlay = () => {},
  onClickSave = () => {},
  onFrameChange = () => {},
  isPlaying = true,
  disableButtons = false,
  onUndoClick = () => {},
  onRedoClick = () => {},
  canUndo = false,
  canRedo = false,
  onClickNext,
  onClickPrevious,
  onFrameClick = () => {},
  timeline = [],
  onTimeChange = () => {},
  durationInput = '',
  onDurationChange = () => {},
  timelineError = null,
  shortened = false,
  playbackMode = 'auto',
  onPlaybackModeChange = () => {},
  playsOnce = false,
  playbackEnded = false,
  compositionMode = 'source',
  onCompositionModeChange = () => {},
}) => {
  const [settingsOpen, setSettingsOpen] = useState(!!timelineError || !!durationInput || playbackMode !== 'auto');
  const settingsId = useId();
  useEffect(() => { if (timelineError) setSettingsOpen(true); }, [timelineError]);
  const timings = useMemo(() => images.map(describeImageTiming), [images]);
  const starts = useMemo(() => timelineStartTimes(timeline), [timeline]);
  const duration = timeline.reduce((sum, frame) => sum + frame.delay, 0);
  const currentTime = playbackEnded ? duration : starts[absoluteCurrentIndex] || 0;
  const tracks = useMemo(() => images.map((image, imageIndex) =>
    timeline.flatMap((frame, index) => {
      const frameIndex = frame.imageIndices[imageIndex];
      return frameIndex < 0 || (index > 0 && frameIndex === timeline[index - 1].imageIndices[imageIndex])
        ? [] : [{ frameIndex, time: starts[index], position: starts[index] / duration * 100 }];
    })), [images, timeline, starts, duration]);
  const allEmptyFrameIndices = useMemo(() => {
    return images.map((image) =>
      image.frames.reduce((acc, frame, index) => {
        if (!!frame.isEmpty) {
          acc.push(index);
        }
        return acc;
      }, [])
    );
  }, [images]);

  return (
    <Card className="player" elevation={0}>
      <div className="player-controls">
        <div className="player-history">
          <Box display="flex" flexDirection="row">
            <ToolTippedIconButton
              // eslint-disable-next-line
              iconButtonProps={{ onClick: onUndoClick, ['aria-label']: 'Undo' }}
              tooltipProps={{ title: 'Undo', placement: 'bottom' }}
              disabled={disableButtons || !canUndo}
            >
              <Undo />
            </ToolTippedIconButton>
            <ToolTippedIconButton
              // eslint-disable-next-line
              iconButtonProps={{ onClick: onRedoClick, ['aria-label']: 'Redo' }}
              tooltipProps={{ title: 'Redo', placement: 'bottom' }}
              disabled={disableButtons || !canRedo}
            >
              <Redo />
            </ToolTippedIconButton>
          </Box>
        </div>
        <div className="player-transport">
          <ToolTippedIconButton
            iconButtonProps={{
              variant: 'text',
              size: 'small',
              // eslint-disable-next-line
              ['aria-label']: 'Previous Frame',
              onClick: onClickPrevious,
            }}
            tooltipProps={{ title: 'Previous Frame', placement: 'bottom' }}
          >
            <SkipPrevious />
          </ToolTippedIconButton>
          <ToolTippedIconButton
            tooltipProps={{
              title: isPlaying ? 'Pause' : 'Play',
              placement: 'bottom',
            }}
            iconButtonProps={{
              onClick: onClickPlay,
              // eslint-disable-next-line
              ['aria-label']: 'play/pause',
              'aria-pressed': isPlaying,
            }}
            disabled={disableButtons || !!timelineError}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </ToolTippedIconButton>
          <ToolTippedIconButton
            tooltipProps={{
              title: 'Next Frame',
              placement: 'bottom',
            }}
            iconButtonProps={{
              variant: 'text',
              size: 'small',
              // eslint-disable-next-line
              ['aria-label']: 'next frame',
              onClick: onClickNext,
            }}
          >
            <SkipNext />
          </ToolTippedIconButton>
        </div>
        <div className="player-save">
          <Button
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={onClickSave}
            disabled={disableButtons || !!timelineError}
          >
            Save GIF
          </Button>
        </div>
      </div>
      <div className="timeline-panel">
        <div className="timeline-heading">
          <div className="timeline-title"><strong>Timeline</strong>
            <span className="timeline-playback-badge">{playsOnce ? 'Plays once' : 'Loops forever'}</span>
          </div>
          {images.length > 1 && <div className="timeline-mode">
            <span>Mode</span>
            <div className="timeline-mode-toggle" role="group" aria-label="Composition mode">
              {['repeat', 'truncate'].map(mode => <button key={mode} type="button"
                aria-pressed={compositionMode === mode} disabled={disableButtons}
                onClick={() => { if (compositionMode !== mode) onCompositionModeChange(mode); }}>
                {mode === 'repeat' ? <Repeat fontSize="small" /> : <ContentCut fontSize="small" />}
                {mode === 'repeat' ? 'Repeat' : 'Truncate'}
              </button>)}
            </div>
          </div>}
        </div>
        <p className="timeline-mode-description">{timings.every(timing => timing.isStatic)
          ? 'Still images stay visible throughout.' : compositionMode === 'repeat'
          ? 'Animations loop to finish together.' : compositionMode === 'truncate'
            ? 'Ends with the shortest animation.' : 'Uses this image’s saved play count.'}</p>
        {compositionMode === 'repeat' && timings.some(timing => !timing.isStatic && timing.playCount > 0) &&
          <p className="timeline-source-note">Saved play counts are overridden in Repeat mode.</p>}
        <div className="timeline-clock">
          <Typography variant="body2" component="span">
          {(currentTime / 1000).toFixed(2)} s / {(duration / 1000).toFixed(2)} s
          </Typography>
          <span>Original speed</span>
        </div>
        <input className="timeline-scrubber" type="range" aria-label="Timeline time"
          aria-valuetext={`${(currentTime / 1000).toFixed(2)} seconds`}
          value={currentTime} min={0} max={Math.max(10, duration)} step={10}
          disabled={disableButtons || !timeline.length}
          onChange={event => onTimeChange(Number(event.target.value))} />
        <button type="button" className="timeline-settings-toggle" aria-expanded={settingsOpen}
          aria-controls={settingsId} onClick={() => setSettingsOpen(!settingsOpen)}>
          <span aria-hidden="true">{settingsOpen ? '−' : '+'}</span> Output settings
        </button>
        {settingsOpen && <div id={settingsId} className="timeline-settings">
          <div className="timeline-options">
          <label>Output duration (seconds)
            <input value={durationInput} inputMode="decimal" aria-invalid={!!timelineError}
              onChange={event => onDurationChange(event.target.value)} disabled={disableButtons}
              placeholder="Full timeline" />
          </label>
          <label>Output playback
            <select value={playbackMode} disabled={disableButtons}
              onChange={event => onPlaybackModeChange(event.target.value)}>
              <option value="auto">{compositionMode === 'repeat' ? 'Auto (repeat forever)' : 'Auto (respect source plays)'}</option>
              <option value="once">Play once</option>
              <option value="forever">Repeat forever</option>
            </select>
          </label>
          </div>
        <Typography variant="caption" color="text.secondary">
          Leave duration blank for the full timeline. Shorter outputs cut without changing speed.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {playsOnce ? 'The preview and saved GIF play once, then stop.'
            : 'The preview and saved GIF repeat. Every output loop restarts all layers.'}
        </Typography>
        {compositionMode === 'repeat' && <Typography variant="caption" color="text.secondary">
          Repeat mode overrides each image’s saved play count.
        </Typography>}
        </div>}
        {timelineError && <Alert severity="warning">{timelineError}</Alert>}
        {shortened && !timelineError && <Alert severity="info">
          Shortened output: the final source frame may be cut short, and looping may jump at the cut.
        </Alert>}
      </div>
      <div className="timeline-tracks-heading"><strong>Images</strong><span>Click a dot to edit its frame</span></div>
      {images.map((image, index) => (
        <ImageFrameSlider
          entries={tracks[index]}
          timePosition={duration ? currentTime / duration * 100 : 0}
          timingLabel={timings[index].isStatic ? 'Still — visible throughout'
            : `${Number((timings[index].duration / 1000).toFixed(2))} s per play · ${compositionMode === 'repeat'
              ? duration ? `${Number((duration / timings[index].duration).toFixed(2))}× in output` : 'Repeats to fit'
              : compositionMode === 'truncate' ? !duration ? 'Awaiting duration' : duration < timings[index].duration ? `Cut at ${Number((duration / 1000).toFixed(2))} s` : 'Full animation'
                : image.playCount ? `${image.playCount === 1 ? 'Plays once' : `${image.playCount} plays`} · ${image.endBehavior === 'hide' ? 'hides' : 'holds'} at end` : 'Loops forever'}`}
          finishedPosition={compositionMode === 'source' && image.playCount && image.frames.length > 1 && duration
            ? Math.min(100, timings[index].duration * image.playCount / duration * 100) : 100}
          key={index}
          startIndex={image.startIndex}
          imageName={image.name}
          isHighlighted={image.name === highlightedImageName}
          imageFrameCount={image.frames.length}
          numFrames={totalNumFrames}
          onClickIncrease={() => {
            onFrameChange(index, 1);
          }}
          onClickDecrease={() => onFrameChange(index, -1)}
          onFrameClick={onFrameClick}
          absoluteCurrentIndex={absoluteCurrentIndex}
          emptyFrameIndices={allEmptyFrameIndices[index]}
        />
      ))}
    </Card>
  );
};

Player.propTypes = {
  compositionMode: PropTypes.string,
  onCompositionModeChange: PropTypes.func,
  playbackEnded: PropTypes.bool,
  playbackMode: PropTypes.string,
  onPlaybackModeChange: PropTypes.func,
  playsOnce: PropTypes.bool,
  timeline: PropTypes.array,
  onTimeChange: PropTypes.func,
  durationInput: PropTypes.string,
  onDurationChange: PropTypes.func,
  timelineError: PropTypes.string,
  shortened: PropTypes.bool,
  images: PropTypes.arrayOf(imageShape).isRequired,
  highlightedImageName: PropTypes.string,
  totalNumFrames: PropTypes.number.isRequired,
  onClickPlay: PropTypes.func,
  onClickSave: PropTypes.func,
  onFrameChange: PropTypes.func,
  onClickNext: PropTypes.func,
  onClickPrevious: PropTypes.func,
  absoluteCurrentIndex: PropTypes.number.isRequired,
  isPlaying: PropTypes.bool,
  disableButtons: PropTypes.bool,
  onUndoClick: PropTypes.func,
  onRedoClick: PropTypes.func,
  canUndo: PropTypes.bool,
  canRedo: PropTypes.bool,
  onFrameClick: PropTypes.func,
};

export default React.memo(Player);
